// A mode's routes, as line rows: number, ends, the provider's id.
//
// LOADED, NOT IMPORTED. The table is a few KB, but it is one city's and only for
// a phone that rides the mode, and the first load carries no city's data but the
// one it needs (tests/entry-chunk.test.js). It is asked for when the mode is
// turned on, when a phone starts with it on, and when one of its routes is named
// anywhere; until it arrives a route is still "Route 96", worked out from its
// id, and the rows fill in their ends a moment later because the table is a ref
// every reader below depends on.
//
// The stops, the strip and everything else is `src/mode-stops.js`, which is
// loaded when one of the mode's screens is opened or the mode is turned on.

import { shallowRef } from 'vue';
import { loadModeData, modeKey } from './mode-data.js';
import { routeNumberOf } from './modes.js';

/** One reactive slot per mode: the route table once loaded, null before. */
const tables = new Map();
const loading = new Map();

function slot(mode) {
  const key = modeKey(mode);
  if (!tables.has(key)) tables.set(key, shallowRef(null));
  return tables.get(key);
}

/** The route table as loaded, or null. Reactive. */
export const routeTableOf = (mode) => slot(mode).value;

/** Load the route table, once. Resolves to null if the chunk cannot be read. */
export function loadModeRoutes(mode) {
  const ref = slot(mode);
  const key = modeKey(mode);
  if (ref.value) return Promise.resolve(ref.value);
  if (!loading.has(key)) {
    loading.set(key, loadModeData(mode, 'routes').then((table) => {
      if (!table) { loading.delete(key); return null; }
      ref.value = table;
      return table;
    }));
  }
  return loading.get(key);
}

/** Tests only: put a table in place, or take it away. */
export function useModeRoutes(mode, raw) {
  slot(mode).value = raw ?? null;
  loading.delete(modeKey(mode));
}

/**
 * One route as a line row, in the shape `lineList` carries a train line in.
 *
 * `name` is "Route 96", which is what a notification title, a disruption screen
 * and a journey leg call it. `ends` is "East Brunswick ↔ St Kilda Beach", which
 * is what the ledger row prints beside the badge — because a number alone says
 * nothing to somebody who does not ride it. Before the table has loaded a route
 * is still a route, with its number and no ends; the load is asked for at the
 * same time.
 */
export function modeLineRow(mode, routeId, routes = routeTableOf(mode)) {
  const r = routes?.routes?.[String(routeId)];
  const word = mode.labels.line.charAt(0).toUpperCase() + mode.labels.line.slice(1);
  if (!r) {
    const n = routeNumberOf(mode, routeId);
    if (routes || !n) return null;
    loadModeRoutes(mode);
    return { routeId: Number(routeId), name: `${word} ${n}`, short: n, ends: '', colour: mode.badge.colour, mode: mode.id };
  }
  // A route named "City - Doncaster via Eastern Fwy" has its road after the last
  // end. The row prints the two ends; the road is `via`, for the places with room.
  const via = /\s+via\s+(.+)$/i.exec(String((r.ends ?? []).at(-1) ?? ''))?.[1] ?? '';
  const ends = (r.ends ?? []).map((e) => String(e).replace(/\s+via\s+.+$/i, ''));
  return {
    routeId: Number(routeId),
    name: `${word} ${r.number}`,
    short: r.number,
    ends: ends.join(' ↔ '),
    ...(via ? { via } : {}),
    colour: mode.badge.colour,
    mode: mode.id,
  };
}

/** Every route, lowest number first: 1, 3, 5, 6 … 109. Empty until loaded. */
export function modeLineRows(mode, routes = routeTableOf(mode)) {
  return Object.keys(routes?.routes ?? {})
    .map((id) => modeLineRow(mode, id, routes))
    .filter(Boolean)
    .sort((a, b) => Number(a.short) - Number(b.short));
}

/** The provider's own route id for a line, for the one place that needs it. */
export function providerRouteIdOf(mode, routeId, routes = routeTableOf(mode)) {
  return Number(routes?.routes?.[String(routeId)]?.ptvRouteId) || null;
}
